import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { FeedInfo } from '../types';
import { EditableUrl } from './EditableUrl';
import { ModalOverlay } from './ModalOverlay';
import { formatDate } from '../utils/formatTime';

export interface FeedInformationProps {
  info: FeedInfo;
  created_at?: string;
  updated_at?: string;
  /** If set, shows an "Admin Site" link for admins. */
  site_id?: string;
  isAdmin?: boolean;
  /** If provided, fields are editable and a Save button appears. */
  onSave?: (updated: FeedInfo) => void;
  /** If provided, a Delete button appears. */
  onDelete?: () => void;
}

/**
 * Displays the core fields of a feed source (feed_url, title, description).
 * Accepts any object satisfying FeedInfo — both Feed and Site qualify.
 * URLs render as blue links with an inline edit icon; other fields use inputs.
 * All editing is opt-in via callbacks; omit onSave for a read-only view.
 */
export function FeedInformation({
  info,
  created_at,
  updated_at,
  site_id,
  isAdmin,
  onSave,
  onDelete,
}: FeedInformationProps) {
  const [feedUrl, setFeedUrl] = useState(info.feed_url);
  const [title, setTitle] = useState(info.title || '');
  const [description, setDescription] = useState(info.description || '');
  const [confirming, setConfirming] = useState(false);

  const handleSave = () => {
    onSave?.({ feed_url: feedUrl, title: title || undefined, description: description || undefined });
  };

  const handleConfirmDelete = () => {
    onDelete?.();
    setConfirming(false);
  };

  const feedLabel = info.title || info.feed_url;

  return (
    <div>
      {(created_at || updated_at) && (
        <div className="site-meta">
          {created_at && <span>Added {formatDate(created_at)}</span>}
          {updated_at && updated_at !== created_at && <span>Updated {formatDate(updated_at)}</span>}
        </div>
      )}
      <div className="site-form-grid">
        <EditableUrl
          label="Feed URL"
          value={feedUrl}
          onChange={setFeedUrl}
        />
        {onSave && (
          <>
            <label>
              <span>Title</span>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label className="site-notes-row">
              <span>Description</span>
              <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
          </>
        )}
        {!onSave && (info.title || info.description) && (
          <>
            {info.title && (
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Title</span>
                <span>{info.title}</span>
              </div>
            )}
            {info.description && (
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Description</span>
                <span className="muted-text" style={{ fontSize: '0.9rem' }}>{info.description}</span>
              </div>
            )}
          </>
        )}
      </div>
      {(onSave || onDelete || (isAdmin && site_id)) && (
        <div className="site-card-actions">
          {onSave && (
            <button className="secondary-button" onClick={handleSave}>Save</button>
          )}
          {onDelete && (
            <button className="ghost-button danger-button" onClick={() => setConfirming(true)}>Delete</button>
          )}
          {isAdmin && site_id && (
            <Link className="secondary-button link-button admin-button" to={`/sites/${site_id}`}>
              Admin Site
            </Link>
          )}
        </div>
      )}
      {confirming && (
        <ModalOverlay onClose={() => setConfirming(false)} className="confirm-modal">
            <h2 style={{ margin: '0 0 12px', fontSize: '1.05rem', color: 'var(--ink)' }}>
              Delete feed?
            </h2>
            <p style={{ margin: '0 0 20px', color: 'var(--ink)', lineHeight: 1.5 }}>
              Delete &ldquo;{feedLabel}&rdquo;? This will remove the feed and stop ingesting its items.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="ghost-button" onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button
                className="primary-button"
                style={{ background: '#b91c1c', borderColor: '#b91c1c' }}
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
        </ModalOverlay>
      )}
    </div>
  );
}
